import { some } from 'lodash';
import { createContext, useContext } from 'react';
import { type CellObject, useTable } from '@/features/overview-table/hooks/use-table';
import type { RmData } from '@/graphql/types';
import { tableData } from '@/reports/data-last-night-table';
import { useDashboard } from '../../../context/dashboard-context';
import { useHotel } from '../../../context/hotel-context';
import { DateHelpers } from '../../../helpers/date-helpers';
import { defaultSubId } from '../../../reducers/use-rate-shop-params';
import { useMeeting } from '../../meetings/context/meeting-context';
import {
  type OtaiLowestRatesQuery,
  useOtaiLowestRatesQuery,
} from '../../overview-table/gql/_gen_/rate-shop.gql';
import { type EventWidgetQuery, useEventWidgetQuery } from '../gql/_gen_/hotel-events-widget.gql';
import { type ReportDataQuery, useReportDataQuery } from '../gql/_gen_/report.gql';
import { type RmRecsWidgetQuery, useRmRecsWidgetQuery } from '../gql/_gen_/rm-recs-widget.gql';
import { useIsShared } from '../hooks/use-report-location';

type ReportDataContextProps = {
  data: ReportDataType;
  loading: boolean;
  refetchRmRecs: () => void;
};

export type DailyRatesData = {
  headers: string[];
  rows: CellObject[][];
};

export type ReportDataType = {
  dataEvents?: EventWidgetQuery;
  dataRateShop?: OtaiLowestRatesQuery;
  dataRateShopBestFlex?: OtaiLowestRatesQuery;
  dataReport?: ReportDataQuery;
  dataRmRecs?: RmRecsWidgetQuery;
  loadingEvents: boolean;
  loadingRateShop: boolean;
  loadingReport: boolean;
  loadingRmRecs: boolean;
  lastNightData: ReturnType<typeof tableData>;
  fiveDayRatesData: DailyRatesData;
  lowestRates: DailyRatesData;
  flexRates: DailyRatesData;
};

type ReportDataProviderProps = { children: React.ReactNode };

const ReportDataContext = createContext<ReportDataContextProps | undefined>(undefined);

function ReportDataProvider({ children }: ReportDataProviderProps) {
  const { meeting } = useMeeting();
  const { brandCode, hotel } = useDashboard();
  const { otaiSubs } = useHotel();
  const { isShared } = useIsShared();

  const coreVariables = {
    brandCode: brandCode!,
    snapshotDate: meeting?.meetingDate || DateHelpers.today(),
    useCache: false,
  };

  const { data: dataEvents, loading: loadingEvents } = useEventWidgetQuery({
    skip: !hotel || !hotel.hotel_id || !hotel.brand_code || isShared,
    variables: {
      hotelId: hotel?.hotel_id || hotel?.brand_code,
      startDate: DateHelpers.subtract(meeting?.meetingDate || DateHelpers.today(), 'days', 7),
      endDate: DateHelpers.subtract(meeting?.meetingDate || DateHelpers.today(), 'days', -365),
    },
  });

  const skipRmRecs = !hotel || !hotel.hotel_id || !hotel.brand_code || isShared;

  const {
    data: dataRmRecs,
    loading: loadingRmRecs,
    refetch: refetchRmRecsQuery,
  } = useRmRecsWidgetQuery({
    skip: skipRmRecs,
    variables: {
      filters: {
        hotelId: hotel?.hotel_id || hotel?.brand_code,
      },
    },
  });

  const { data: dataRateShop, loading: loadingRateShop } = useOtaiLowestRatesQuery({
    skip: !brandCode || !otaiSubs || isShared,
    variables: {
      params: {
        subscriptionId: defaultSubId(otaiSubs) as string,
        los: 1,
        bar: false,
        ota: 'branddotcom',
        roomType: 'all',
        shopLength: 90,
        changeDays: [1, 3, 7],
      },
      brandCode: brandCode || '',
    },
  });

  const { data: dataRateShopBestFlex, loading: loadingRateShopBestFlex } = useOtaiLowestRatesQuery({
    skip: !brandCode || !otaiSubs || isShared,
    variables: {
      params: {
        subscriptionId: defaultSubId(otaiSubs) as string,
        los: 1,
        bar: true,
        ota: 'branddotcom',
        roomType: 'all',
        shopLength: 90,
        changeDays: [1, 3, 7],
      },
      brandCode: brandCode || '',
    },
  });

  // We need to compare the first of the year vs the first of the previous month
  // relative to the meeting date and pick the one that goes further back.

  const firstOfYear = DateHelpers.firstOfYear(meeting?.meetingDate || DateHelpers.today());
  const firstOfPrevMonth = DateHelpers.firstOfMonth({
    date: DateHelpers.subtract(meeting?.meetingDate || DateHelpers.today(), 'days', 30),
    daysOut: 0,
  });

  const foyIsBefore = DateHelpers.isBefore(firstOfYear, firstOfPrevMonth);

  const startDate = foyIsBefore ? firstOfYear : firstOfPrevMonth;

  const { data: dataReport, loading: loadingReport } = useReportDataQuery({
    skip: !brandCode || !hotel?.hotel_id || !hotel.brand_code || isShared,
    variables: {
      brandCode: brandCode || '',
      snapshotDate: meeting?.meetingDate || DateHelpers.today(),
      args: {
        brandCode,
        snapshotDate: meeting?.meetingDate || DateHelpers.today(),
        startDate,
        endDate: DateHelpers.subtract(meeting?.meetingDate || DateHelpers.today(), 'days', -365),
      },
      currentRateTableArgs2: {
        ...coreVariables,
        startDate,
        endDate: DateHelpers.subtract(meeting?.meetingDate || DateHelpers.today(), 'days', -365),
      },
      filters: {
        startDate,
        endDate: DateHelpers.subtract(meeting?.meetingDate || DateHelpers.today(), 'days', -365),
        hotelId: hotel?.hotel_id || hotel?.brand_code,
      },
      hotelId: hotel?.hotel_id || hotel?.brand_code,
      startDate,
      endDate: DateHelpers.subtract(meeting?.meetingDate || DateHelpers.today(), 'days', -365),
      groupDailyStartDate2: meeting?.meetingDate || DateHelpers.today(),
      groupDailyEndDate2: DateHelpers.subtract(
        meeting?.meetingDate || DateHelpers.today(),
        'days',
        -365,
      ),
    },
  });

  // Add lastNightData calculation
  const tableDates = DateHelpers.lastNightTableDates(meeting?.meetingDate || DateHelpers.today());
  const lastNightData = tableData(
    DateHelpers.subtract(meeting?.meetingDate || DateHelpers.today(), 'days', 1),
    dataReport?.rmData?.filter((rm) =>
      DateHelpers.betweenOrEqual(rm?.stay_date, tableDates.mtdStart, tableDates.mtdEnd),
    ) as RmData[],
    dataReport?.rmData?.filter((rm) =>
      DateHelpers.betweenOrEqual(rm?.stay_date_ly, tableDates.mtdStartLy, tableDates.mtdEndLy),
    ) as RmData[],
  );
  // ************************ RATE TABLES ************************
  const tableCols = dataRateShop?.compSet?.length
    ? dataRateShop.compSet.filter((comp) => comp?.key !== 'rates.0')
    : [];

  // Add five-day rates calculation

  const { headers: detailHeaders, rows: detailRows } = useTable({
    customHeaders: null,
    data: {
      dailyRates: dataRateShop?.dailyRates,
      compSet: dataRateShop?.compSet,
      rmData: dataReport?.rmData,
    },
    dateSegment: 'day',
    includeSummary: false,
    startDate: meeting?.meetingDate || DateHelpers.today(),
    endDate: DateHelpers.addDays(meeting?.meetingDate || DateHelpers.today(), 4),
    detailCols: [
      { dataSource: 'rmData', key: 'dow' },
      { dataSource: 'dailyRates', key: 'stay_date' },
      { dataSource: 'rmData', key: 'occ' },
      { dataSource: 'rmData', key: 'ooo' },
      {
        dataSource: 'rmData',
        key: 'delta_7day',
        tooltip: '7-day delta (change in rooms committed)',
      },
      ...tableCols,
    ],
  });

  const fiveDayRatesData = {
    headers: detailHeaders.map((h) => h.title),
    rows: detailRows.map((row) => row.map((cell) => cell)),
  };

  // ************

  // 90 day lowest rate
  const { headers: headersLowest, rows: rowsLowest } = useTable({
    customHeaders: null,
    data: {
      dailyRates: dataRateShop?.dailyRates,
      compSet: dataRateShop?.compSet,
      rmData: dataReport?.rmData,
    },
    dateSegment: 'day',
    includeSummary: false,
    startDate: meeting?.meetingDate || DateHelpers.today(),
    endDate: DateHelpers.addDays(meeting?.meetingDate || DateHelpers.today(), 90),
    detailCols: [
      { dataSource: 'dailyRates', key: 'stay_date' },
      { dataSource: 'rmData', key: 'dow' },

      ...tableCols,
    ],
  });

  const lowestRates = {
    headers: headersLowest.map((h) => h.title),
    rows: rowsLowest.map((row) => row.map((cell) => cell)),
  };

  // ************

  // ************

  // 90 day lowest rate
  const { headers: headersFlex, rows: rowsFlex } = useTable({
    customHeaders: null,
    data: {
      dailyRates: dataRateShopBestFlex?.dailyRates,
      compSet: dataRateShopBestFlex?.compSet,
      rmData: dataReport?.rmData,
    },
    dateSegment: 'day',
    includeSummary: false,
    startDate: meeting?.meetingDate || DateHelpers.today(),
    endDate: DateHelpers.addDays(meeting?.meetingDate || DateHelpers.today(), 90),
    detailCols: [
      { dataSource: 'dailyRates', key: 'stay_date' },
      { dataSource: 'rmData', key: 'dow' },
      ...tableCols,
    ],
  });

  const flexRates = {
    headers: headersFlex.map((h) => h.title),
    rows: rowsFlex.map((row) => row.map((cell) => cell)),
  };

  // ************

  // ************************ RATE TABLES ************************

  const data = {
    dataEvents,
    dataReport,
    dataRmRecs,
    loadingEvents,
    loadingRateShop: loadingRateShop || loadingRateShopBestFlex,
    loadingReport,
    loadingRmRecs,
    lastNightData,
    fiveDayRatesData,
    lowestRates,
    flexRates,
  };

  const loading = some(
    [loadingEvents, loadingRateShop, loadingRateShopBestFlex, loadingReport, loadingRmRecs],
    Boolean,
  );

  // This provider stays mounted across routes, so the RM recs query is only fetched once.
  // Rec statuses change outside the report (e.g. ACCEPTED -> PROCESSING -> UPLOADED after a
  // rate upload), so the report refetches them when it is opened.
  const refetchRmRecs = () => {
    if (!skipRmRecs) {
      refetchRmRecsQuery();
    }
  };

  const value = {
    data,
    loading,
    refetchRmRecs,
  };

  return <ReportDataContext.Provider value={value}>{children}</ReportDataContext.Provider>;
}

function useReportData() {
  const context = useContext(ReportDataContext);
  if (context === undefined) {
    throw new Error('useReportData must be used within a ReportDataProvider');
  }
  return context;
}

export { ReportDataProvider, useReportData };
